import { Globe } from 'lucide-preact'
import { type WebViewDto } from '@/api/webs'
import type { WebTabKey } from '@/hooks/useWebTabRouter'
import { useOnlineEventSpeakers } from '@/hooks/useOnlineEventSpeakers'
import { usePullAgenda } from '@/hooks/usePullAgenda'
import { PullAgendaModal } from '../modals/PullAgendaModal'
import { useOnlineEventForm, type SpeakerUiWithPic } from '@/hooks/useOnlineEventForm'
import { useBigMarkerIntegration } from '@/hooks/useBigMarkerIntegration'
import { SpeakerListSection } from './SpeakerListSection'
import { AgendaSection } from './AgendaSection'
import { OnlineEventDetailsCard } from './OnlineEventDetailsCard'
import { OverviewCard } from './OverviewCard'
import { SpeakerEditors } from './SpeakerEditors'
import { SignedOffNote } from './SignedOffNote'
import { isOrbitalXRoom } from '@/utils/onlineEventMode'

export function OnlineEventPanel({ web, onPatchWeb, isSignedOff, importingSpeakers, routeToTab }: { web: WebViewDto; onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void; isSignedOff?: boolean; importingSpeakers?: boolean; routeToTab?: (tab: WebTabKey) => void }) {
  const evt = web.OnlineEvent
  // A room web's ConferenceId is our own room id, so nothing below may read it as a BigMarker conference (ORB-1086).
  const isRoom = isOrbitalXRoom(evt)
  const bigMarkerConference = !isRoom && !!evt?.ConferenceId
  const { integrationError, integrationName } = useBigMarkerIntegration(web.WorkspaceId)
  const speakers: SpeakerUiWithPic[] = (evt?.Speakers as SpeakerUiWithPic[]) || []

  const speakerHook = useOnlineEventSpeakers({ web, onPatchWeb })
  const agendaPull = usePullAgenda({ web, onPatchWeb })
  const {
    editingDetails, setEditingDetails, savingDetails, setSavingDetails,
    editingOverview, setEditingOverview, savingOverview, setSavingOverview,
    anySectionEditing,
    register, watch, setValue, getFieldState, formState, control,
    agendaFields, append, remove, move,
    saving, hasDirtyEmpty, normalizedInitial, agendaChanged,
    editingAgenda, setEditingAgenda,
    saveAgenda, cancelAgendaEdit,
  } = useOnlineEventForm({ web, onPatchWeb, speakers, showAddParticipant: speakerHook.showAddParticipant, showEditModal: speakerHook.showEditModal })
  // An Overwrite changes web.OnlineEvent, and useOnlineEventForm resets the whole shared form when it
  // does, so a pull must not run while another section holds unsaved edits, nor an edit start mid-pull.
  const editLocked = anySectionEditing || agendaPull.pulling

  return (
    <>
      {isSignedOff && (
        <SignedOffNote
          locked="event details, speakers, and agenda"
          // The exceptions: BigMarker can't be sent an agenda (ORB-943), and host links work in every stage (ORB-1086).
          stillAllowed={isRoom
            ? 'You can still copy and email host links.'
            : bigMarkerConference ? 'You can still pull the agenda from BigMarker.' : undefined}
        />
      )}
      {/* min-w-0 on both columns: grid items default to min-width:auto, so the nowrap
          speaker title in the right column would otherwise widen its track past 1.2fr
          and squeeze Overview/Event details. */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1.2fr] gap-6">
        <div className="min-w-0 space-y-6">
          <OverviewCard
            onlineEvent={evt} web={web}
            editingOverview={editingOverview} savingOverview={savingOverview}
            editDisabled={isSignedOff || editLocked}
            onEditStart={() => setEditingOverview(true)} onEditEnd={() => setEditingOverview(false)}
            onSavingChange={setSavingOverview} onPatchWeb={onPatchWeb}
            watch={watch} setValue={setValue} control={control}
          />
          <OnlineEventDetailsCard
            evt={evt} web={web}
            editing={editingDetails} saving={savingDetails}
            editDisabled={editLocked || isSignedOff}
            integrationError={integrationError}
            integrationName={integrationName}
            onEdit={() => setEditingDetails(true)} onCancel={() => setEditingDetails(false)}
            onSaved={() => setEditingDetails(false)} onSaving={setSavingDetails}
            onPatchWeb={onPatchWeb}
            onOpenRoomTab={routeToTab ? () => routeToTab('room') : undefined}
            control={control} register={register} watch={watch} setValue={setValue}
            getFieldState={getFieldState} formState={formState}
          />
        </div>
        <div className="min-w-0 space-y-4">
          <SpeakerListSection
            speakers={speakers} isSignedOff={isSignedOff} hasConference={bigMarkerConference} importingSpeakers={importingSpeakers}
            room={isRoom ? { workspaceId: web.WorkspaceId, webId: web.Id } : undefined}
            unmatchedPresenters={web.OnlineEvent?.UnmatchedPresenters ?? []}
            dismissingUnmatched={speakerHook.dismissingUnmatched}
            onAddUnmatched={speakerHook.openAddFor}
            onDismissUnmatched={speakerHook.dismissUnmatchedPresenters}
            onAddParticipant={() => speakerHook.openAddFor(null)}
            onEditSpeaker={speakerHook.openEdit}
            onDeleteSpeaker={speakerHook.setDeleteTarget}
            onSaveReorder={speakerHook.handleSaveReorder}
          />
          <AgendaSection
            editing={editingAgenda}
            saving={saving}
            editDisabled={isSignedOff || editLocked}
            initialItems={normalizedInitial}
            agendaChanged={agendaChanged}
            hasDirtyEmpty={hasDirtyEmpty}
            agendaFields={agendaFields}
            control={control}
            append={append} remove={remove} move={move}
            onEdit={() => setEditingAgenda(true)}
            onSave={saveAgenda}
            onCancel={cancelAgendaEdit}
            pullAgenda={bigMarkerConference ? { pulling: agendaPull.pulling, onPull: agendaPull.pull, disabled: anySectionEditing } : undefined}
            // 'landing-page', not 'media': Deploy lives on the Landing Page tab, and 'media' is Assets.
            agendaUpdatedNotice={agendaPull.justUpdated
              ? { onGoToLandingPage: routeToTab ? () => routeToTab('landing-page') : undefined, onDismiss: agendaPull.dismissUpdated }
              : undefined}
          />
        </div>
      </div>
      {agendaPull.comparison && (
        <PullAgendaModal
          comparison={agendaPull.comparison}
          overwriting={agendaPull.overwriting}
          onOverwrite={agendaPull.overwrite}
          onCancel={agendaPull.cancel}
        />
      )}
      <SpeakerEditors
        workspaceId={web.WorkspaceId} webId={web.Id} campaignId={web.CampaignId}
        {...speakerHook}
      />
      {isSignedOff && routeToTab && (
        <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Landing Page</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Generate a landing page for this event to drive registrations.
              </p>
            </div>
            <button
              type="button"
              onClick={() => routeToTab('media')}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              <Globe className="w-4 h-4" />
              Generate Landing Page
            </button>
          </div>
        </div>
      )}
    </>
  )
}
